/**
 * lib/mix.ts — the arithmetic of the mix: the next track brought to the playing one's tempo, its
 * beats laid on the playing one's, kept there, and the speed brought back to the deck's tempo.
 *
 * Task 47. `spec.md` «Микс и дополнения плеера», «Микс: темп и фаза». Pure: numbers in, numbers
 * out, like `lib/transition.ts`, which asks this module when the mode is `mix` and turns its answer
 * into the overlap; `engine/player.ts` writes what they say into the decks' speeds and gains.
 * `test/mix.test.ts` pins every rule here on numbers alone.
 *
 * ## Speeds as a share of the deck's tempo
 *
 * A deck's tempo (±8 %, task 23) is the speed of a record nobody touches. A mix speed is a share
 * of it — a **factor**: 1 is the tempo itself, and «more than 8 % from the tempo» is a factor
 * further than 0,08 from 1. Since task 55 each deck has a tempo of its own, and the `factor` given
 * here for the playing deck is its speed counted in the next deck's tempo (`engine/player.ts`,
 * «Two equal decks»): the arithmetic below is the same whether the two tempos are one or not.
 *
 * ## The tempo
 *
 * The next deck plays at `playing BPM × playing factor / next BPM`. Counted half or double — 90
 * and 180 — is the same tempo, so the next BPM goes to the octave nearest the playing one first.
 * A factor further than {@link MAX_STRETCH} from 1 would be heard as a stretch: then there is no
 * mix, only a short blend of {@link SHORT_BLEND_S} at the tempo (story 102).
 *
 * ## The beats
 *
 * The playing track's beats are at `lead-in + offset + n × 60 / BPM` of its file; the next track's,
 * at its own. Which of two eighths is «one» the server cannot tell (`lib/beat-pattern.ts`), so the
 * next track's first beat goes half a beat on when the two drum patterns say so — and only at the
 * same octave: a pattern is one beat long, and a beat counted double is another length. The
 * overlap then starts at the last moment, no later than where a blend of the mix's length would,
 * at which the next deck's first beat, reached at its speed, falls on a beat of the playing one.
 *
 * ## Kept together
 *
 * A `play()` lands within a tick, the decks' clocks are not one, and two measured tempos are a
 * hundredth or so apart: the beats drift. Every tick the difference is read from the two decks'
 * seconds and the deck that follows gets a nudge against it, proportional to it and never more
 * than {@link NUDGE_MAX} — a change of speed that small is not heard; under {@link NUDGE_FROM_S},
 * none. Before the overlap's middle
 * the next deck follows the playing one; after it, the old one follows the new.
 *
 * ## Back to the tempo
 *
 * After the overlap the new track goes back from its factor to 1 in {@link RETURN_S}, evenly
 * (story 109): a set of an hour must not drift away from the tempo the listener chose.
 */

import type { TrackBeat } from './api';
import { halfBeatShift } from './beat-pattern';

/** The confidence of both measures from which the beats are laid on beats (`spec.md`: 0,6). */
export const MIX_CONFIDENCE = 0.6;

/** The mix is this many beats of the playing track… */
export const MIX_BEATS = 16;
/** …and no shorter… */
export const MIX_MIN_S = 6;
/** …and no longer, in seconds. */
export const MIX_MAX_S = 20;

/** The furthest from the deck's tempo the next track is brought. */
export const MAX_STRETCH = 0.08;

/** The blend of two tempos too far apart to bring together. */
export const SHORT_BLEND_S = 4;

/** The largest nudge of a speed against a drift: half a per cent. */
export const NUDGE_MAX = 0.005;

/** A drift of this many seconds, or more, gets the whole {@link NUDGE_MAX}. */
export const NUDGE_FULL_S = 0.025;

/**
 * A drift under this many seconds gets no nudge: no ear hears it, and the difference of two decks
 * that are together is a hair off 0 by the arithmetic alone — each would be a write of the speed,
 * and at 1 a flip of the tone switch.
 */
export const NUDGE_FROM_S = 0.001;

/** The new track is back at the deck's tempo this many seconds after the overlap. */
export const RETURN_S = 30;

/** Measures this close to the edge are on it: 100 and 108 BPM are 8 % apart, not a hair more. */
const EPSILON = 1e-9;

/** The speed of the next deck, and the octave its BPM was taken at. */
export interface TempoFit {
  /** A share of the deck's tempo — see «Speeds as a share of the deck's tempo». */
  readonly factor: number;
  /** The next BPM was multiplied by 2 to this power: 0 — as measured. */
  readonly octave: number;
}

/** Both tracks' beats, each in seconds of its own file. */
export interface BeatGrid {
  /** The playing track's first beat. */
  readonly firstS: number;
  /** Its beat. */
  readonly periodS: number;
  /** The next track's first beat that goes on a beat of the playing one. */
  readonly nextFirstS: number;
  /** The next track's beat, at the octave of the playing one. */
  readonly nextPeriodS: number;
}

/** What the mix lays down: the next deck's speed, and the two grids its beats keep to. */
export interface MixFit {
  readonly factor: number;
  readonly grid: BeatGrid;
}

/** One side of the mix: a track's beat as the server measured it, and its silence before music. */
export interface MixSide {
  readonly beat?: TrackBeat | null | undefined;
  readonly leadInS?: number | undefined;
}

/** A beat the mix may lay on: a sane tempo, a first beat, and a confidence of 0,6 or more. */
export function sureBeat(beat: TrackBeat | null | undefined): beat is TrackBeat {
  return (
    beat !== null &&
    beat !== undefined &&
    Number.isFinite(beat.bpm) &&
    beat.bpm > 0 &&
    Number.isFinite(beat.offsetS) &&
    beat.offsetS >= 0 &&
    beat.confidence >= MIX_CONFIDENCE
  );
}

/** The next deck's speed — see «The tempo». `undefined` — too far, or numbers that are not. */
export function fitTempo(bpm: number, nextBpm: number, factor: number): TempoFit | undefined {
  if (![bpm, nextBpm, factor].every((value) => Number.isFinite(value) && value > 0)) {
    return undefined;
  }
  // `+ 0`: the same octave is 0, not the −0 of a rounded −0,03.
  const octave = Math.round(Math.log2(bpm / nextBpm)) + 0;
  const fitted = (factor * bpm) / (nextBpm * 2 ** octave);
  if (Math.abs(fitted - 1) > MAX_STRETCH + EPSILON) return undefined;
  return { factor: fitted, octave };
}

/** Sixteen beats of `bpm`, within 6 and 20 seconds. */
export function mixLengthS(bpm: number): number {
  return Math.min(MIX_MAX_S, Math.max(MIX_MIN_S, (MIX_BEATS * 60) / bpm));
}

function seconds(value: number | undefined): number {
  return value !== undefined && Number.isFinite(value) && value > 0 ? value : 0;
}

/** `value` within `[0, period)`, also for a negative one. */
function wrap(value: number, period: number): number {
  return ((value % period) + period) % period;
}

/**
 * The mix of `playing` into `next`, the playing deck at `factor`: `'apart'` — tempos too far apart
 * to bring together; `undefined` — a measure missing or unsure, and the transition is a blend.
 */
export function fitMix(
  playing: MixSide,
  next: MixSide,
  factor: number,
): (TempoFit & { readonly grid: BeatGrid }) | 'apart' | undefined {
  const ours = playing.beat;
  const theirs = next.beat;
  if (!sureBeat(ours) || !sureBeat(theirs)) return undefined;
  const fit = fitTempo(ours.bpm, theirs.bpm, factor);
  if (fit === undefined) return Number.isFinite(factor) && factor > 0 ? 'apart' : undefined;
  const periodS = 60 / ours.bpm;
  const ownPeriodS = 60 / theirs.bpm;
  const nextPeriodS = ownPeriodS / 2 ** fit.octave;
  const shift = fit.octave === 0 ? halfBeatShift(ours.profile, theirs.profile).shiftBeats : 0;
  return {
    ...fit,
    grid: {
      firstS: seconds(playing.leadInS) + ours.offsetS,
      periodS,
      nextFirstS: seconds(next.leadInS) + wrap(theirs.offsetS + shift * ownPeriodS, nextPeriodS),
      nextPeriodS,
    },
  };
}

/**
 * The playing track's second at which the next deck starts from `nextFromS`, so that its first
 * beat on the grid falls on a beat of the playing one — the last such second at or before
 * `wantS`, and never before the file's start. See «The beats».
 */
export function alignedStart(
  grid: BeatGrid,
  wantS: number,
  nextFromS: number,
  factor: number,
  nextFactor: number,
): number {
  // The next deck's way to its first beat, in seconds of the playing track.
  const leadS = ((grid.nextFirstS - nextFromS) * factor) / nextFactor;
  const n = Math.floor((wantS + leadS - grid.firstS) / grid.periodS);
  const startS = grid.firstS + n * grid.periodS - leadS;
  return startS >= 0 ? startS : startS + grid.periodS * Math.ceil(-startS / grid.periodS);
}

/**
 * How far the next deck's beats are ahead of the playing one's, in seconds of the playing track:
 * positive — ahead, negative — behind, never more than half a beat either way, since a beat goes
 * on the nearest beat. `positionS` and `nextPositionS` are the two decks' seconds of their files.
 */
export function phaseErrorS(grid: BeatGrid, positionS: number, nextPositionS: number): number {
  const beats =
    (nextPositionS - grid.nextFirstS) / grid.nextPeriodS - (positionS - grid.firstS) / grid.periodS;
  return (beats - Math.round(beats)) * grid.periodS;
}

/**
 * The share added to the following deck's speed against a drift of `errorS` of the next deck
 * ahead: proportional, at most {@link NUDGE_MAX} either way. The next deck takes it as it is; an old
 * deck that follows the new one after the middle takes it with the sign turned.
 */
export function nudge(errorS: number): number {
  if (!Number.isFinite(errorS) || Math.abs(errorS) < NUDGE_FROM_S) return 0;
  const share = (-errorS / NUDGE_FULL_S) * NUDGE_MAX;
  return Math.min(NUDGE_MAX, Math.max(-NUDGE_MAX, share));
}

/** The factor `elapsedS` into the way back from `from` to the deck's tempo — see «Back to the tempo». */
export function returnFactor(from: number, elapsedS: number): number {
  const share = Math.min(1, Math.max(0, elapsedS / RETURN_S));
  return share === 1 ? 1 : from + (1 - from) * share;
}

/**
 * The next track's second to start from when its `play()` comes with the playing track at
 * `positionS` rather than at the plan's start: as far on as the playing track is late, in the next
 * track's seconds, so that its beats still fall where they were laid. Never before its file's start.
 */
export function startFromS(
  plan: {
    readonly startAtS: number;
    readonly nextFromS: number;
    readonly mix?: MixFit | undefined;
  },
  positionS: number,
  factor: number,
): number {
  if (plan.mix === undefined) return plan.nextFromS;
  const lateS = positionS - plan.startAtS;
  return Math.max(0, plan.nextFromS + (lateS * plan.mix.factor) / factor);
}
